import React, { useState, useRef, useEffect, useMemo, Fragment } from 'react'
import {
  Chip,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  Paper,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import CloseIcon from '@material-ui/icons/Close'
import { TextField } from 'material-ui'
import TooltipModal from '../../../../../Components/TooltipModal'
import { TEXT_FIELD_STYLES, STEPS } from './constants'
import { formatLocations } from './utils'

const ModalSecondStep = ({
  classes,
  setCurrentStep,
  handleGenerate,
  values,
  selectedLocations,
  setSelectedLocations,
  selectedPostcodes,
  postcodeResults,
  loading,
  locationsCount,
}) => {
  const [showLocationResults, setShowLocationResults] = useState(false)
  const [locationSearch, setLocationSearch] = useState('')

  const locationSearchRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        locationSearchRef.current &&
        !locationSearchRef.current.contains(event.target)
      ) {
        setShowLocationResults(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleLocationSearch = e => {
    const value = e.target.value
    setLocationSearch(value)
  }

  const handleLocationInputFocus = () => {
    setShowLocationResults(true)
  }

  const handleLocationSelect = location => {
    if (!selectedLocations.some(selected => selected.id === location.id)) {
      setSelectedLocations([...selectedLocations, location])
    }
    setLocationSearch('')
    setShowLocationResults(false)
  }

  const handleRemoveLocation = location => {
    setSelectedLocations(
      selectedLocations.filter(loc => loc.id !== location.id),
    )
  }

  const handleBack = () => {
    setCurrentStep(STEPS.FIRST)
    setSelectedLocations([])
    setLocationSearch('')
    setShowLocationResults(false)
  }

  const isLimitExceeded = useMemo(
    () => selectedLocations.length == values.countOfMissions,
    [selectedLocations.length, values.countOfMissions],
  )

  const locations = useMemo(
    () =>
      selectedPostcodes.length
        ? formatLocations(selectedPostcodes)
        : formatLocations(postcodeResults),
    [selectedPostcodes, postcodeResults],
  )

  const locationResults = useMemo(
    () =>
      locations.filter(
        location =>
          location &&
          location.name &&
          location.name.toLowerCase().includes(locationSearch.toLowerCase()) &&
          !selectedLocations.some(
            selected => selected && selected.id === location.id,
          ),
      ),
    [locations, locationSearch, selectedLocations],
  )

  return (
    <DialogContent>
      <div className={classes.container}>
        {selectedPostcodes.length > 0 ? (
          <Fragment>
            <div
              style={{ marginBottom: '22px' }}
              className={classes.zipSearchContainer}
            >
              <div
                style={{ marginBottom: '-2px' }}
                className={classes.inputLabel}
              >
                <span>Your selected zip codes</span>
              </div>
              <div className={classes.chipContainer}>
                {selectedPostcodes.map(item => (
                  <Chip
                    key={item.postcode}
                    label={item.postcode}
                    className={classes.chip}
                    style={{ marginTop: '8px' }}
                    clickable={false}
                  />
                ))}
                <div
                  onClick={handleBack}
                  style={{ marginTop: '8px' }}
                  className={classes.editZipCodes}
                >
                  Edit zip codes
                </div>
              </div>
            </div>
            <div className={classes.limit}>
              Locations in Selected Zips: {locationsCount}
            </div>
          </Fragment>
        ) : null}
        <div className={classes.zipSearchContainer}>
          <div style={{ marginBottom: '4px' }} className={classes.inputLabel}>
            <span>Whitelist locations (optional)</span>
            <TooltipModal content="The number of locations should be equal to or less than the total number of missions" />
          </div>
          <div
            style={{ marginTop: '4px', marginBottom: '8px' }}
            className={classes.helper}
          >
            {isLimitExceeded ? (
              <span>
                Add all <strong>locations within the zip code areas</strong>{' '}
                that you want to include in the generated mission
              </span>
            ) : (
              <span>
                If zip codes were selected, only locations within those ZIP
                codes will be available. Otherwise, you'll be able to choose any
                location within the city.
              </span>
            )}
          </div>
          <TextField
            id="location-search"
            type={'text'}
            underlineShow={false}
            style={TEXT_FIELD_STYLES.field}
            inputStyle={TEXT_FIELD_STYLES.input}
            value={locationSearch}
            onChange={handleLocationSearch}
            onFocus={handleLocationInputFocus}
            placeholder="Search locations"
            disabled={isLimitExceeded}
          />
          <div
            style={{
              ...TEXT_FIELD_STYLES.searchIcon,
              top: isLimitExceeded ? 83 : 106,
            }}
          >
            <SearchIcon style={TEXT_FIELD_STYLES.iconColor} />
          </div>
          {showLocationResults && (
            <Paper
              ref={locationSearchRef}
              className={classes.zipResults}
              elevation={3}
            >
              {loading ? (
                <div className={classes.placeholder}>Loading...</div>
              ) : locationResults.length > 0 ? (
                <List>
                  {locationResults.map(location => (
                    <ListItem
                      button
                      key={location.id}
                      onClick={() => handleLocationSelect(location)}
                    >
                      <ListItemText primary={location.name} />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <div className={classes.placeholder}>No results found</div>
              )}
            </Paper>
          )}
          <div
            className={classes.limit}
            style={isLimitExceeded ? { color: '#DA0808', fontWeight: 700 } : {}}
          >
            Limit: {selectedLocations.length} / {values.countOfMissions}
          </div>
          <div className={classes.chipContainer}>
            {selectedLocations.map(location => (
              <Chip
                key={location.id}
                label={location.name}
                onDelete={() => handleRemoveLocation(location)}
                className={classes.chip}
                deleteIcon={<CloseIcon style={{ width: 16, height: 16 }} />}
                clickable={false}
                style={{ marginTop: '8px' }}
              />
            ))}
          </div>
        </div>
        <div
          className={classes.modalButtonContainer}
          style={{ marginTop: selectedLocations.length ? '32px' : '72px' }}
        >
          <button className={classes.cancelButton} onClick={handleBack}>
            BACK
          </button>
          <button
            className={loading ? classes.disabledButton : classes.actionButton}
            onClick={handleGenerate}
            disabled={loading}
          >
            {loading ? 'LOADING...' : 'GENERATE'}
          </button>
        </div>
      </div>
    </DialogContent>
  )
}

export default ModalSecondStep
